import styled, { css, keyframes } from "styled-components";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";

/* CollectionFilter — one native horizontal filter row plus optional search. */

const resolveChipWidth = (width) => {
  if (width === undefined || width === null) return "auto";
  return typeof width === "number" ? `${width}px` : width;
};

const shimmer = keyframes`
  0%   { background-position: -468px 0; }
  100% { background-position:  468px 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.7; }
`;

/* Reusable chip base — used by Category (tall) and Tag (smaller) variants */
const chipBase = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 8px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    background 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy),
    color 220ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 12px;
    right: 12px;
    height: 7px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }
`;

/* ─── Wrapper — holds the three rows, exposes the data-collection-filter
   anchor that the sticky header/tabs use to react to scroll. ───────── */

export const FilterWrapper = styled.div`
  position: relative;
  /* Width is controlled by an outer Toolbar wrapper on the page; this
     stays 100% so all toolbar rows (CreateInputBar / Tabs / Filter)
     sit at the same effective width. */
  width: 100%;
  max-width: 100%;

  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  overflow: visible;

  @media (max-width: 768px) {
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    gap: 2px;
  }
`;

/* Folder chip — mirrors CategoryChip visuals but at a smaller scale. */
export const FolderPill = styled.div`
  ${chipBase};
  height: 34px;
  padding: 0 14px;
  font-family: var(--font-family-primary);
  font-size: 12.5px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 600)};
  letter-spacing: -0.01em;
  color: ${({ $selected }) =>
    $selected ? "var(--glass-text-on-pill)" : "var(--cf-text)"};
  text-shadow: ${({ $selected }) =>
    $selected ? "0 1px 0 rgba(var(--color-brand-900-rgb), 0.28)" : "none"};
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "rgba(var(--glass-white-rgb), 0.8)"
        : "rgba(var(--glass-white-rgb), 0.65)"};
  background: ${({ $selected }) =>
    $selected
      ? `radial-gradient(
           140% 100% at 20% 0%,
           rgba(255, 255, 255, 0.3) 0%,
           rgba(255, 255, 255, 0) 55%
         ),
         linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)`
      : `radial-gradient(
           120% 100% at 20% 0%,
           rgba(var(--glass-white-rgb), 0.88) 0%,
           rgba(var(--glass-pale-rgb), 0.66) 55%,
           rgba(var(--glass-pale-rgb), 0.5) 100%
         )`};
  box-shadow: ${({ $selected }) =>
    $selected
      ? `0 2px 5px rgba(var(--color-brand-600-rgb), 0.28),
         0 1px 3px rgba(var(--color-brand-600-rgb), 0.14),
         inset 0 1px 0 rgba(255, 255, 255, 0.6),
         inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3)`
      : `0 1.5px 3px rgba(var(--color-brand-500-rgb), 0.12),
         inset 0 1px 0 rgba(255, 255, 255, 0.92),
         inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.2)`};

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
    color: white;
    box-shadow: ${({ $selected }) =>
      $selected
        ? `0 2px 6px rgba(0, 0, 0, 0.4),
           inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.18)`
        : `0 2px 6px rgba(0, 0, 0, 0.35),
           inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.14)`};
  }

  ${({ $create }) =>
    $create &&
    css`
      border-style: dashed;
      font-weight: 800;
      color: var(--color-brand-600);
    `}

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    path {
      stroke: ${({ $selected, $create }) => {
        if ($selected) return "var(--glass-text-on-pill)";
        if ($create) return "var(--color-brand-600)";
        return "var(--cf-text)";
      }};
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-1px);
    }
  }

  @media (max-width: 480px) {
    height: 32px;
    padding: 0 12px;
    font-size: 12px;
  }
`;

// ─── Row 1 — Filter row hosting Category scroll + Tag dropdown side-by-side ──

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 0 18px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: auto;
  overscroll-behavior-x: auto;
  scroll-behavior: auto !important;

  > * {
    flex: 0 0 auto;
  }

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

// ─── Row 1 — Category chip (primary, taller, brand-gradient active) ────

export const CategoryChip = styled.div`
  ${chipBase};
  height: ${(p) => (p.$height ? p.$height : "46px")};
  min-width: ${({ $width }) => resolveChipWidth($width)};
  padding: 0 20px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 600)};
  letter-spacing: -0.01em;
  color: ${({ $selected }) =>
    $selected ? "var(--glass-text-on-pill)" : "var(--cf-text)"};
  text-shadow: ${({ $selected }) =>
    $selected ? "0 1px 0 rgba(var(--color-brand-900-rgb), 0.28)" : "none"};
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "rgba(var(--glass-white-rgb), 0.8)"
        : "rgba(var(--glass-white-rgb), 0.65)"};
  background: ${({ $selected }) =>
    $selected
      ? `radial-gradient(
           140% 100% at 20% 0%,
           rgba(255, 255, 255, 0.3) 0%,
           rgba(255, 255, 255, 0) 55%
         ),
         linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)`
      : `radial-gradient(
           120% 100% at 20% 0%,
           rgba(var(--glass-white-rgb), 0.88) 0%,
           rgba(var(--glass-pale-rgb), 0.66) 55%,
           rgba(var(--glass-pale-rgb), 0.5) 100%
         )`};
  box-shadow: ${({ $selected }) =>
    $selected
      ? `0 2px 5px rgba(var(--color-brand-600-rgb), 0.28),
         0 1px 3px rgba(var(--color-brand-600-rgb), 0.14),
         inset 0 1px 0 rgba(255, 255, 255, 0.6),
         inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3)`
      : `0 1.5px 3px rgba(var(--color-brand-500-rgb), 0.12),
         inset 0 1px 0 rgba(255, 255, 255, 0.92),
         inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.2)`};

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
    box-shadow: ${({ $selected }) =>
      $selected
        ? `0 2px 6px rgba(0, 0, 0, 0.4),
           inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.18)`
        : `0 2px 6px rgba(0, 0, 0, 0.35),
           inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.14)`};
  }
  /* selectedGlow keyframe dropped — animating box-shadow forces a full
     repaint per frame even though the chip never moves. */

  ${({ $animation }) =>
    $animation &&
    css`
      background: linear-gradient(
        90deg,
        rgba(var(--glass-pale-rgb), 0.8) 0px,
        rgba(var(--glass-pale-rgb), 0.9) 40px,
        rgba(var(--glass-pale-rgb), 0.8) 80px
      );
      background-size: 468px 100%;
      animation:
        ${shimmer} 3s infinite linear,
        ${pulse} 2s infinite ease-in-out;
      &::before {
        display: none;
      }
    `}

  svg {
    width: 18px;
    height: 18px;
    path {
      stroke: ${({ $selected }) => ($selected ? "#ffffff" : "var(--cf-text)")};
    }
    circle {
      stroke: ${({ $selected }) => ($selected ? "#ffffff" : "var(--cf-text)")};
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-1px);
    }
  }

  @media (max-width: 480px) {
    height: 42px;
    padding: 0 16px;
    font-size: 13px;
  }
`;

// ─── Row 3 — Search collapse wrapper. Animates between hidden and the
//   natural row height. We rely on grid-template-rows 0fr → 1fr because
//   it interpolates smoothly without needing a hard-coded height. ───────
export const SearchCollapse = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: translateY(${({ $open }) => ($open ? "0" : "-4px")});
  transition:
    grid-template-rows 260ms var(--ease-snappy),
    opacity 200ms var(--ease-snappy),
    transform 260ms var(--ease-snappy);
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};

  > * {
    overflow: hidden;
    min-height: 0;
  }
`;

// ─── Row 3 — Search bar (rendered inside SearchCollapse) ───────────────

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

export const SearchBar = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  height: 46px;
  padding: 0 16px;
  align-items: center;
  gap: 10px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--glass-white-rgb), 0.7);
  background:
    radial-gradient(
      120% 100% at 20% 0%,
      rgba(var(--glass-white-rgb), 0.88) 0%,
      rgba(var(--glass-pale-rgb), 0.64) 55%,
      rgba(var(--glass-pale-rgb), 0.5) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.78) 0%,
      rgba(var(--glass-pale-rgb), 0.6) 100%
    );

  box-shadow: ${({ $focused }) =>
    $focused
      ? `0 10px 22px rgba(var(--color-brand-500-rgb), 0.22),
         inset 0 1px 0 rgba(255, 255, 255, 1),
         inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.24)`
      : `0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
         inset 0 1px 0 rgba(255, 255, 255, 0.95),
         inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.2)`};

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
    box-shadow: ${({ $focused }) =>
      $focused
        ? `0 6px 14px rgba(0, 0, 0, 0.45),
           inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.18)`
        : `0 4px 10px rgba(0, 0, 0, 0.4),
           inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.14)`};
  }

  transition:
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 14px;
    right: 14px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.62) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: var(--cf-text);
    path {
      stroke: var(--cf-text);
    }
  }

  @media (max-width: 480px) {
    height: 42px;
    padding: 0 14px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 16px 0 0;
  background-color: transparent;
  border: none;
  outline: none;
  color: var(--cf-text);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.005em;
  position: relative;
  z-index: 1;

  &::placeholder {
    color: var(--cf-text);
    opacity: 0.5;
    font-weight: 400;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 0 14px 0 0;
  }
`;

export const SearchClearButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  margin-right: -4px;
  border: none;
  outline: none;
  border-radius: 50%;
  background: rgba(var(--color-brand-300-rgb), 0.16);
  color: var(--cf-text);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 180ms var(--ease-snappy);

  &:hover {
    background: rgba(var(--color-brand-300-rgb), 0.28);
  }

  svg {
    width: 14px;
    height: 14px;
    path {
      stroke: var(--cf-text);
    }
  }
`;

/* Frosted water icon button (44×44) for the right-side cluster.
   Holds Favorites, Select-mode, and the "+ Create Folder" plus. */
export const IconButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  padding: 0;
  border-radius: 50%;
  border: 1px solid
    ${({ $active }) =>
      $active
        ? "rgba(var(--glass-white-rgb), 0.85)"
        : "rgba(var(--glass-white-rgb), 0.6)"};
  background: ${({ $active }) =>
    $active
      ? `radial-gradient(
           140% 100% at 20% 0%,
           rgba(255, 255, 255, 0.3) 0%,
           rgba(255, 255, 255, 0) 55%
         ),
         linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)`
      : `radial-gradient(
           130% 100% at 30% 15%,
           rgba(var(--glass-white-rgb), 0.82) 0%,
           rgba(var(--glass-white-rgb), 0.54) 45%,
           rgba(var(--glass-pale-rgb), 0.42) 100%
         )`};
  box-shadow: ${({ $active }) =>
    $active
      ? `0 2.5px 6px rgba(var(--color-brand-600-rgb), 0.32),
         inset 0 1px 0 rgba(255, 255, 255, 0.55),
         inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3)`
      : `0 3px 7px rgba(var(--color-brand-500-rgb), 0.14),
         inset 0 1px 0 rgba(255, 255, 255, 0.95),
         inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22)`};

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
    box-shadow: ${({ $active }) =>
      $active
        ? `0 2.5px 6px rgba(0, 0, 0, 0.45),
           inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.2)`
        : `0 3px 7px rgba(0, 0, 0, 0.4),
           inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.16)`};
  }
  color: ${({ $active }) =>
    $active ? "var(--glass-text-on-pill)" : "var(--cf-text)"};
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    background 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy),
    color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 6px;
    width: 18px;
    height: 10px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, ${({ $active }) => ($active ? 0.6 : 0.85)}) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 18px;
    height: 18px;
    position: relative;
    z-index: 1;
    color: inherit;
    path,
    circle {
      stroke: currentColor;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-1px);
    }
  }

  /* selectedGlow keyframe dropped — see note in CategoryChip above. */

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

/* Small counter pill used inline when Select mode is ON — shows n/20.
   Renders INSIDE IconButton when active, replacing the checkbox glyph. */
export const SelectCounter = styled.span`
  position: relative;
  z-index: 1;
  color: inherit;
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: -0.005em;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

// ─── Anchor element — data-collection-filter attribute lives here ──────
//  Used by the Tabs / Header scroll logic as the "below-fold" reference.
//  Kept on the outer FadeWrapper to preserve external behavior.

export const FadeWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 4;
`;

// ─── Legacy exports (kept so any stray imports continue to resolve) ────

export const Container = styled.div`
  display: none;
`;

export const CategoryItem = CategoryChip;
export const FolderItem = CategoryChip;
export const CategoryText = styled.span`
  color: inherit;
  font-family: var(--font-family-primary);
  font-weight: inherit;
`;

export const SearchFilter = SearchBar;
export const StyledInput = SearchInput;

export const CloseIconWrapper = styled.div`
  display: none;
`;
export const CloseIcon = styled(CloseSvg)`
  path {
    stroke: var(--cf-text);
  }
`;

/* Leading search-glyph that morphs into an X. Used inside ExpandSearch as
   the toggle area — it always handles the click (expand → close), so it
   stays interactive in both states. Morph animation mirrors
   CreateInputBar's LeadingIconWrap so both inputs share the same
   micro-interaction language. */
export const SearchMorphIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  color: var(--cf-text);
  transition: transform 220ms var(--ease-snappy);

  &:hover {
    transform: scale(1.06);
  }
  &:active {
    transform: scale(0.94);
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(var(--color-brand-400-rgb), 0.35);
    border-radius: 999px;
  }

  svg {
    width: 18px;
    height: 18px;
    overflow: visible;
  }

  svg path {
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 1;
    transition: stroke-dashoffset 420ms cubic-bezier(0.7, 0, 0.3, 1);
  }

  .icon-search {
    stroke-dashoffset: ${({ $active }) => ($active ? 1 : 0)};
    transition-delay: ${({ $active }) => ($active ? "0ms" : "260ms")};
  }

  .icon-x-1 {
    stroke-dashoffset: ${({ $active }) => ($active ? 0 : 1)};
    transition-delay: ${({ $active }) => ($active ? "260ms" : "0ms")};
  }

  .icon-x-2 {
    stroke-dashoffset: ${({ $active }) => ($active ? 0 : 1)};
    transition-delay: ${({ $active }) => ($active ? "380ms" : "80ms")};
  }

  @media (prefers-reduced-motion: reduce) {
    svg path {
      transition: none;
    }
    &:hover,
    &:active {
      transform: none;
    }
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

/* Expandable search pill — collapsed it looks identical to the round
   IconButton (44×44); expanded it grows in width to reveal the input
   inline. Width transitions; overflow:hidden clips the input while
   collapsed. */
export const ExpandSearch = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 44px;
  width: ${({ $expanded }) => ($expanded ? "320px" : "44px")};
  max-width: 100%;
  border-radius: 999px;
  border: 1px solid
    ${({ $focused, $expanded }) =>
      $expanded
        ? "rgba(var(--glass-white-rgb), 0.85)"
        : "rgba(var(--glass-white-rgb), 0.6)"};
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.82) 0%,
    rgba(var(--glass-white-rgb), 0.54) 45%,
    rgba(var(--glass-pale-rgb), 0.42) 100%
  );
  box-shadow: ${({ $focused }) =>
    $focused
      ? `0 10px 22px rgba(var(--color-brand-500-rgb), 0.22),
         inset 0 1px 0 rgba(255, 255, 255, 1),
         inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.24)`
      : `0 3px 7px rgba(var(--color-brand-500-rgb), 0.14),
         inset 0 1px 0 rgba(255, 255, 255, 0.95),
         inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22)`};

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
    box-shadow: ${({ $focused }) =>
      $focused
        ? `0 6px 14px rgba(0, 0, 0, 0.45),
           inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.18)`
        : `0 3px 7px rgba(0, 0, 0, 0.4),
           inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.14)`};
  }

  overflow: hidden;
  transition:
    width 320ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 8px;
    width: 18px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  @media (max-width: 480px) {
    height: 40px;
    width: ${({ $expanded }) => ($expanded ? "240px" : "40px")};
  }
`;

export const Relative = styled.div`
  position: relative;
  margin-top: 2px;
`;

export const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: var(--radius-lg);
  background: var(--glass-base-1);
  box-shadow: var(--shadow-panel);
`;
export const InfoTitle = styled.span`
  color: var(--cf-text);
  font-weight: 700;
  font-size: 14px;
`;
export const InfoText = styled.span`
  color: var(--cf-text);
  opacity: 0.7;
  font-size: 13px;
`;

export const ViewModeToggle = styled.button`
  /* kept for any ViewMode callsite outside this file */
`;
export const ViewModeGridIcon = styled.div``;

/* Inline edit affordance sitting at the right edge of a folder pill.
   Stops click propagation in JS so it opens the edit modal without
   switching the active filter. */
export const FolderPillEdit = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: 0 -4px 0 2px;
  border-radius: 999px;
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "rgba(var(--glass-white-rgb), 0.45)"
        : "rgba(var(--color-brand-300-rgb), 0.32)"};
  background: ${({ $selected }) =>
    $selected
      ? "rgba(var(--glass-white-rgb), 0.18)"
      : "rgba(var(--glass-white-rgb), 0.7)"};
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  padding: 0;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy);

  svg {
    width: 11px;
    height: 11px;
    path {
      stroke: ${({ $selected }) =>
        $selected ? "#ffffff" : "var(--cf-text)"} !important;
      fill: none !important;
    }
  }

  &:hover {
    transform: scale(1.08);
    border-color: ${({ $selected }) =>
      $selected
        ? "rgba(var(--glass-white-rgb), 0.85)"
        : "rgba(var(--color-brand-400-rgb), 0.6)"};
    background: ${({ $selected }) =>
      $selected
        ? "rgba(var(--glass-white-rgb), 0.32)"
        : "rgba(var(--glass-pale-rgb), 0.95)"};
  }

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
  }
`;
